import { Inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AppEnvironments } from '@models/app-environments';
import { UserStatusResponse } from '@models/user-response';
import {
  StatefulService
} from '@services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { brandPortalNames } from '@shared/enums/brand-portal-names';
import { CompanyCode } from '@shared/enums/company-code';
import { CompanyHost } from '@shared/enums/company-host';
import { BehaviorSubject, Observable } from 'rxjs';
import { WINDOW } from '../window';
import { CacheService } from './cache.service';

/** Contains State */
@Injectable({
  providedIn: 'root',
})
export class AppConfigService implements StatefulService {
  private _apiUrl: string = null;
  private _assetUrl: string = null;
  private _siteStatus: BehaviorSubject<boolean>;
  private _ltiLaunchPresentationReturnUrl: string = null;

  siteStatusMessage: string = '';
  siteTitle: string = 'Site Maintenance';
  siteStatus: Observable<boolean>;
  inSkofApp = false;
  IFRAME_ALLOW_LIST: string = 'microphone';

  // store the URL so we can redirect after logging in.
  redirectUrl: string;

  /**
   * The login brand represents brand identified by the URL when the app is loaded.
   */
  loginBrand$: BehaviorSubject<CompanyCode> = new BehaviorSubject(null);

  constructor(
    private cache: CacheService,
    @Inject(WINDOW) private appWindow: Window,
  ) {
    this.useProxyApi();
    this._siteStatus = new BehaviorSubject<boolean>(true);
    this.siteStatus = this._siteStatus.asObservable();
  }

  get apiUrl(): string {
    return this._apiUrl;
  }

  set apiUrl(value: string) {
    this._apiUrl = value;
  }

  get viewerModuleUrl() {
    return '/viewer';
  }

  get avatarUrl(): string {
    return `${this._assetUrl}avatar`;
  }

  set assetUrl(assetUrl: string) {
    this._assetUrl = assetUrl;
  }

  get assetUrl(): string {
    return this._assetUrl;
  }

  get sseSiteUrl(): string {
    return `${this.assetUrl}sites/student`;
  }

  get skwrlSiteUrl(): string {
    return `${this.assetUrl}sites/skwl`;
  }

  setSiteStatus(value: boolean) {
    this._siteStatus.next(value);
  }

  get isProduction(): boolean {
    return environment.environment === AppEnvironments.prod;
  }

  get isHighlightsPortalUrl(): boolean {
    return this.loginBrand === CompanyCode.HighlightsPortal;
  }

  get getBrandTitle(): brandPortalNames {
    return this.isHighlightsPortalUrl ? brandPortalNames.heg : brandPortalNames.zb;
  }

  getAuthUrl(path: string): string {
    return `${this.apiUrl}/${path}`;
  }

  setLoginBrand(value: CompanyCode) {
    this.loginBrand$.next(value);
  }

  get loginBrand(): CompanyCode {
    if (this.loginBrand$.value === null) {
      this.setBrandFromUrl();
    }
    return this.loginBrand$.value;
  }

  get environment(): AppEnvironments {
    return environment.environment;
  }

  setBrandFromUrl() {
    // Set loginBrand based on the URL
    let loginBrand = CompanyCode.ZBPortal;
    if (window.location.hostname.includes(CompanyHost.HighlightsPortal)) {
      loginBrand = CompanyCode.HighlightsPortal;
    }
    this.setLoginBrand(loginBrand);
  }

  useProxyApi(enabled: boolean = false) {
    if (enabled && environment.proxyApiUrl) {
      this._apiUrl = environment.proxyApiUrl;
    } else {
      this._apiUrl = environment.apiUrl;
    }
  }

  clearCustomData(): void {
    this._assetUrl = null;
  }

  setCustomUrls(data: UserStatusResponse) {
    this._assetUrl = data.assetUrl;
  }

  set ltiExternalUrl(value: string) {
    this._ltiLaunchPresentationReturnUrl = value;
  }

  get ltiExternalUrl(): string {
    return this._ltiLaunchPresentationReturnUrl;
  }

  get pdfViewerUrl(): string {
    return '/assets/pdfjs/web/viewer.html';
  }

  get pdfViewerLockedUrl(): string {
    return '/assets/pdfjs/web/viewer-locked.html';
  }

  brandOptions<T>(options: {
    highlights?: T,
    zbportal?: T,
  }): T | undefined {
    switch (this.loginBrand) {
    case CompanyCode.HighlightsPortal:
      return options.highlights;
    default:
      return options.zbportal;
    }
  }

  get isLocalUrl(): boolean {
    return this.checkForInternalUrl();
  }

  checkForInternalUrl(): boolean {
    const windowLocation = window.location.host.toLocaleLowerCase();
    return windowLocation === 'zbportal.internal:4200'
      || windowLocation === 'highlightsportal.internal:4200'
      || windowLocation === 'localhost:4200'
      || windowLocation ===  '10.0.2.2';
  }

  /**
   * Gets origin from window.location.
   *
   * Can replace port when running locally.
   * @param replacePort If true, if local URL and port is 4200, port is replaced with 4300.
   */
  getOrigin(replacePort = true): string {
    let { origin } = this.appWindow.location;
    if (replacePort && this.checkForInternalUrl()) {
      origin = origin.replace('4200', '4300');
    }
    return origin;
  }

  /**
   * Configuration for Filament - sending allowed permissions for iFrame
   */
  getFilamentIframeAttributes(): Record<string, string> {
    return {
      allow: this.IFRAME_ALLOW_LIST
    };
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.redirectUrl = null;
    this._ltiLaunchPresentationReturnUrl = null;
    this._assetUrl = null;
    this._siteStatus.next(true);
  }
}
